import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import React from "react";

import {
  SpinNumberInputField,
  SubmissionForm,
  ToggleField,
  Separator,
  Stack,
  useTheme,
  UseFormMethods,
  DropdownField,
  IDropdownOption
} from "@bps/fluent-ui";
import { usePutLocation } from "@libs/api/gateways/best-health/practice/best-health-ops-gateway.hooks";
import {
  PracticeLocation,
  PracticeLocationUpdate
} from "@libs/api/gateways/best-health/practice/best-health-ops-gateway.dtos";

const toggleStyles = {
  root: {
    marginBottom: 0
  }
};

interface PracticeLocationConfigProps {
  location: PracticeLocation;
}
export const PracticeLocationConfig: React.FC<PracticeLocationConfigProps> = ({
  location
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.BhbPracticeManagementWrite
  );

  const putMutation = usePutLocation(location.tenantId);

  const onSubmit = async (
    updates: PracticeLocationUpdate,
    actions: Pick<UseFormMethods<PracticeLocationUpdate>, "reset" | "setValue">
  ) => {
    await putMutation.mutateAsync({
      locationId: location.id,
      updates
    });
    actions.reset(updates);
  };

  const theme = useTheme();
  return (
    <SubmissionForm<PracticeLocationUpdate>
      initialValues={{
        onlineBookingsEnabled: location.bookingSettings?.enabled,
        createNewPatientRecord: location.createNewPatientRecord,
        allowCancellationsEnabled: location.allowCancellationsEnabled,
        maximumAppointmentsPerDay: location.maximumAppointmentsPerDay,
        minimumTimeToBook: location.minimumTimeToBook,
        showAvailability: location.showAvailability
      }}
      onSubmit={onSubmit}
      hideCancelButton
    >
      {() => {
        return (
          <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              tokens={{
                childrenGap: theme.spacing.l2
              }}
            >
              <Stack.Item>
                <ToggleField
                  label="Accepts New Patients"
                  name="onlineBookingsEnabled"
                  styles={toggleStyles}
                  disabled={!hasWritePermission}
                  title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
                />

                <ToggleField
                  label="Create Patient Record"
                  name="createNewPatientRecord"
                  styles={toggleStyles}
                  disabled={!hasWritePermission}
                  title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
                />
              </Stack.Item>
              <Separator vertical />
              <Stack.Item>
                <ToggleField
                  label="Allow Cancel"
                  name="allowCancellationsEnabled"
                  styles={toggleStyles}
                  disabled={!hasWritePermission}
                  title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
                />

                <SpinNumberInputField
                  label="Min Time To Book"
                  name="minimumTimeToBook"
                  disabled={!hasWritePermission}
                  suffix="mins"
                />
              </Stack.Item>
              <Separator vertical />
              <Stack.Item>
                <SpinNumberInputField
                  label="Max Appts Per Day"
                  name="maximumAppointmentsPerDay"
                  disabled={!hasWritePermission}
                />
                <DropdownField
                  label="Weeks in advance"
                  name="showAvailability"
                  disabled={!hasWritePermission}
                  options={[...new Array(12)].map((_, index) => {
                    const weekCount = index + 1;

                    return {
                      key: weekCount,
                      text: weekCount.toString()
                    } as IDropdownOption;
                  })}
                />
              </Stack.Item>
            </Stack>
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};
